import { useMemo } from 'react';
import ToggleTabButton from 'src/components/primitives/ToggleTabs/ToggleTabButton';
import { ToggleTab } from 'src/components/primitives/ToggleTabs/types';
import { cm } from 'src/utils/classNames';

const DEFAULT_PADDING = 8;

const LONG_SHORT_COLORS = [
	{
		activeBg: 'bg-positive-green',
		activeText: 'text-container-bg',
		inactive: 'text-text-positive-green-button',
	},
	{
		activeBg: 'bg-negative-red',
		activeText: 'text-container-bg',
		inactive: 'text-text-negative-red-button',
	},
] as const;

interface ToggleTabsProps<T extends string> {
	options: ToggleTab<T>[];
	activeTab: T;
	onTabChange: (tab: T) => void;
	variant?: 'plain' | 'longShort';
	/**
	 * Classes for the container.
	 */
	className?: string;
	/**
	 * Padding of the container. Defaults to `4px`.
	 *
	 * Passed separately from `className` because its value is necessary for
	 * calculating the animated background styles.
	 */
	padding?: number;
	/**
	 * Classes for the tab buttons. When a tab is selected, the classes included
	 * in `selectedTabClassName` will take precedence.
	 */
	tabClassName?: string;
	selectedTabClassName?: string;
	animatedBackgroundClassName?: string;
	dataTestId?: string;
	tabTestIdPrefix?: string;
}

export default function ToggleTabs<T extends string>({
	options,
	activeTab,
	onTabChange,
	variant = 'plain',
	className,
	padding: paddingFromProps,
	tabClassName,
	selectedTabClassName,
	animatedBackgroundClassName,
	dataTestId,
	tabTestIdPrefix,
}: ToggleTabsProps<T>) {
	const padding = paddingFromProps ?? DEFAULT_PADDING;

	const containerStyles = useMemo(() => {
		return { padding };
	}, [padding]);

	const animatedBackgroundSize = useMemo(() => {
		return {
			width: `calc(${100 / options.length}% - ${padding}px)`,
			height: `calc(100% - ${padding * 2}px)`,
		};
	}, [padding, options.length]);

	const activeTabIndex = useMemo(
		() => options.findIndex((tab) => tab.value === activeTab),
		[options, activeTab]
	);

	const animatedBackgroundStyles = useMemo(() => {
		const left = !activeTabIndex
			? `${padding}px`
			: `${100 * (activeTabIndex / options.length)}%`;

		return {
			...animatedBackgroundSize,
			left,
		};
	}, [activeTabIndex, animatedBackgroundSize, options.length, padding]);

	return (
		<div
			className={cm(
				'relative flex w-full items-center rounded-3xl border border-stroke-divider bg-container-bg',
				className
			)}
			style={containerStyles}
			data-testid={dataTestId}
		>
			{options.map((tab, index) => {
				const longShortColor =
					variant === 'longShort' ? LONG_SHORT_COLORS[index] : undefined;
				return (
					<ToggleTabButton
						key={tab.value}
						label={tab.label}
						className={cm(
							tabClassName,
							activeTab === tab.value && selectedTabClassName
						)}
						activeClassName={longShortColor?.activeText}
						inactiveClassName={longShortColor?.inactive}
						isActive={activeTab === tab.value}
						onClick={() => onTabChange(tab.value)}
						disabled={tab.disabled}
						data-testid={
							tabTestIdPrefix ? `${tabTestIdPrefix}-${tab.value}` : undefined
						}
					/>
				);
			})}

			<div
				className={cm(
					'absolute rounded-3xl transition-all duration-200 ease-out-smooth',
					variant === 'longShort'
						? cm(
								LONG_SHORT_COLORS[activeTabIndex]?.activeBg,
								animatedBackgroundClassName
							)
						: cm('border border-button-primary-bg', animatedBackgroundClassName)
				)}
				style={animatedBackgroundStyles}
			/>
		</div>
	);
}
